/**
 * useAuth 훅
 *
 * 책임:
 * - AuthContext에서 토큰 + 사용자 정보 읽기
 * - login: ID/비밀번호로 로그인 → 토큰 저장
 * - logout: 토큰 제거 + Context 초기화
 * - isLoggedIn: 로그인 상태 boolean
 *
 * 모든 컨테이너에서 사용
 */

import { useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import * as endpoints from '../api/endpoints';

export interface UseAuthReturn {
  token: string | null;
  user: { id: string; name: string } | null;
  login: (id: string, password: string) => Promise<void>;
  logout: () => void;
  isLoggedIn: boolean;
}

export function useAuth(): UseAuthReturn {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  // 로그인: endpoints.login 호출 → 응답의 토큰 + 사용자 정보를 Context에 저장
  const login = useCallback(
    async (id: string, password: string) => {
      const response = await endpoints.login(id, password);
      context.setAuth(response.token, response.user);
    },
    [context]
  );

  // 로그아웃: AuthContext.logout 호출 → 토큰 + 사용자 제거
  const logout = useCallback(() => {
    context.logout();
  }, [context]);

  return {
    token: context.token,
    user: context.user,
    login,
    logout,
    isLoggedIn: context.token !== null,
  };
}
